import React from "react";
import { css } from "@emotion/css";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { Callout } from "@octopusdeploy/design-system-components";
import { BackgroundTaskSummaryResource } from "client/resources/backgroundTaskSummaryResource";
import { BackgroundTaskStatus } from "client/resources/backgroundTaskStatus";
import PaperLayout from "components/PaperLayout/PaperLayout";
import SimpleDataTable from "components/SimpleDataTable/SimpleDataTable";
import { DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent";
import { Refresh } from "components/DataBaseComponent/DataBaseComponent";
import ActionList from "components/ActionList";
import ActionButton from "components/Button";
import repository from "client/repository";
import { ReefResource } from "client/resources/reefResource";
import { ReefStatus } from "client/resources/reefStatus";
import splitStringIntoWords from "utils/StringHelper/splitStringIntoWords";
import {
    HostedScriptsRolloutResource,
    HostedScriptsVersionResource
} from "client/resources/hostedScriptsRolloutResource";
import {HostedScriptsRolloutState} from "client/resources/hostedScriptsRolloutState";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import DateFormatter from "utils/DateFormatter";
import routeLinks from "routeLinks";
import OverflowMenu from "components/Menu/OverflowMenu";
import Markdown from "components/Markdown";
import EditHostedScriptsVersionNotesDialog from "./EditHostedScriptsVersionNotesDialog";
import { latestRolloutsByVersionAndReef, ReefRollouts } from "./hostedScriptsRolloutGrid";
import { reefsRouteLinks } from "./reefsRouteLinks";
import { sortReefs } from "./Reefs";

// Sticky cells need an explicit background or rows scroll underneath them, and the reef column has to
// outrank the header row where the two intersect.
const gridStyle = css`
    overflow: auto;
    max-height: calc(100vh - 16rem);

    table {
        border-collapse: separate;
        border-spacing: 0;
        // DataTable is width: 100%, which spreads a handful of columns across the whole page.
        width: auto;
    }

    thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: ${themeTokens.color.background.primary.default};
        white-space: nowrap;
        vertical-align: top;
    }

    thead th:first-of-type {
        left: 0;
        z-index: 3;
    }

    tbody td:first-of-type {
        position: sticky;
        left: 0;
        z-index: 1;
        background: ${themeTokens.color.background.primary.default};
        white-space: nowrap;
    }

    tbody td {
        white-space: nowrap;
    }
`;

const regionStyle = css`
    display: block;
    color: ${themeTokens.color.text.secondary};
`;

// Matches [ProcessId] on BulkChangeHostedScriptVersionsBackgroundProcess.
const BulkChangeHostedScriptVersionsProcessId = "BulkChangeHostedScriptVersionsBackgroundProcess";

const bulkUpdateStyle = css`
    margin-bottom: ${space["16"]};
`;

const versionHeaderStyle = css`
    display: flex;
    align-items: center;
    gap: ${space["4"]};
`;

const notesStyle = css`
    color: ${themeTokens.color.text.secondary};
    font-weight: normal;
    font-size: 0.8125rem;
    line-height: 1.4;
    margin-top: ${space["4"]};
    max-width: 16rem;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;

    p {
        margin: 0;
    }
`;

interface State extends DataBaseComponentState {
    reefs?: ReefResource[];
    versions?: HostedScriptsVersionResource[];
    rollouts?: Map<string, ReefRollouts>;
    regionsById?: Map<string, string>;
    bulkUpdates?: BackgroundTaskSummaryResource[];
}

class RolloutDataTable extends SimpleDataTable<ReefResource> {
}

export class HostedScriptsRollout extends DataBaseComponent<{}, State> {
    constructor(props: {}) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            // Slower than the 5s most pages use, since a rollout step takes minutes and a poll costs
            // four requests.
            this.doRefresh = await this.startRefreshLoop(() => this.loadData(), 30000);
        });
    }

    render() {
        const versions = this.state.versions ?? [];

        return <PaperLayout title="Hosted Scripts Rollout"
                            busy={this.state.busy}
                            errors={this.state.errors}
                            sectionControl={<ActionList actions={[
                                <ActionButton label="Refresh" onClick={this.doRefresh}/>
                            ]}/>}
                            fullWidth={true}>
            {this.state.bulkUpdates?.map(task =>
                <div key={task.Id} className={bulkUpdateStyle}>
                    <Callout type="information" title="Bulk hosted scripts update in progress">
                        <InternalLink to={routeLinks.backgroundTasks.task(task.Id).root}>{task.Id}</InternalLink>
                    </Callout>
                </div>)}
            {this.state.reefs && this.state.versions && (versions.length === 0
                ? <div>No hosted scripts rollouts recorded</div>
                : <div className={gridStyle}>
                    <RolloutDataTable
                        data={this.state.reefs}
                        headerColumns={["Reef", ...versions.map(v => this.buildVersionHeader(v))]}
                        onRow={reef => this.buildRow(reef, versions)}
                        onEmpty={<div>No reefs found</div>}/>
                </div>)}
        </PaperLayout>;
    }

    private doRefresh: Refresh = () => Promise.resolve();

    private async loadData() {
        const [versionsPage, reefs, azureRegions, bulkUpdates] = await Promise.all([
            repository.HostedScripts.listVersions(),
            repository.Reefs.list(),
            repository.AzureRegions.list(),
            repository.BackgroundTasks.listSummaries({
                processIds: [BulkChangeHostedScriptVersionsProcessId],
                statuses: [BackgroundTaskStatus.Running, BackgroundTaskStatus.Sleeping],
            }),
        ]);
        const versions = versionsPage.Resources;
        // An empty filter would fetch every rollout ever recorded, so only ask once there is a page to fill.
        const rollouts = versions.length === 0
            ? []
            : await repository.HostedScripts.listRollouts({versions: versions.map(v => v.Version)});

        sortReefs(reefs, azureRegions);
        return {
            reefs,
            versions,
            rollouts: latestRolloutsByVersionAndReef(rollouts),
            regionsById: new Map(azureRegions.map(r => [r.Id, r.Region as string])),
            bulkUpdates: bulkUpdates.Resources,
        };
    }

    private buildVersionHeader(version: HostedScriptsVersionResource) {
        return <>
            <div className={versionHeaderStyle}>
                {/* Only releases are tagged in HostedScripts; pre-release versions (e.g. 18.3140.0-branch) are not. */}
                {version.Version.includes("-")
                    ? <code>{version.Version}</code>
                    : <ExternalLink href={`https://github.com/OctopusDeploy/HostedScripts/tree/${version.Version}`}>
                        <code>{version.Version}</code>
                    </ExternalLink>}
                <OverflowMenu menuItems={[
                    OverflowMenu.dialogItem("Edit notes",
                        <EditHostedScriptsVersionNotesDialog
                            version={version.Version}
                            notes={version.Notes}
                            onChange={this.doRefresh}/>)
                ]}/>
            </div>
            {version.Notes && <div className={notesStyle} title={version.Notes}>
                <Markdown markup={version.Notes}/>
            </div>}
        </>;
    }

    private buildRow = (reef: ReefResource, versions: HostedScriptsVersionResource[]) => {
        return [
            <>
                <InternalLink to={reefsRouteLinks.reef(reef.Id).root}>{reef.Name}</InternalLink>
                <span className={regionStyle}>{this.state.regionsById?.get(reef.AzureRegionId)}</span>
                {/* A reef mid provisioning has no rollout row yet, so its status is the only sign
                    anything is happening. */}
                {reef.Status !== ReefStatus.Live &&
                    <span className={regionStyle} style={{color: reefStatusColour(reef.Status)}}>
                        {splitStringIntoWords(reef.Status)}
                    </span>}
            </>,
            ...versions.map(v => displayRolloutState(
                this.state.rollouts?.get(v.Version)?.get(reef.Id),
                reef.KubernetesClusters.length)),
        ];
    };
}

function reefStatusColour(status: ReefStatus): string {
    switch (status) {
        case ReefStatus.ProvisioningFailed: return themeTokens.color.text.danger;
        case ReefStatus.Deleting: return themeTokens.color.text.warning;
        default: return themeTokens.color.text.info;
    }
}

export function displayRolloutState(clusters: HostedScriptsRolloutResource[] | undefined, clusterCount: number) {
    if (!clusters || clusters.length === 0) {
        return <span style={{color: themeTokens.color.text.tertiary}}>&mdash;</span>;
    }

    const latest = clusters.reduce((a, b) => a.StartedAt >= b.StartedAt ? a : b);

    return <InternalLink to={routeLinks.backgroundTasks.task(latest.BackgroundTaskId).root}>
        {rolloutStateLabel(clusters, clusterCount)}
    </InternalLink>;
}

function rolloutStateLabel(clusters: HostedScriptsRolloutResource[], clusterCount: number) {
    if (clusters.some(c => c.State === HostedScriptsRolloutState.InProgress)) {
        return <span style={{color: themeTokens.color.text.info}}>Running</span>;
    }

    // The word as well as the date, so failure is not carried by colour alone.
    if (clusters.some(c => c.State === HostedScriptsRolloutState.Failed)) {
        return <span style={{color: themeTokens.color.text.danger}}>
            Failed {DateFormatter.dateToShortFormat(latestOf(clusters.map(c => c.FailedAt)))}
        </span>;
    }

    // A half migrated reef, which must not read as done.
    if (clusters.length < clusterCount) {
        return <span style={{color: themeTokens.color.text.warning}}>
            {clusters.length} of {clusterCount} clusters
        </span>;
    }

    return <span style={{color: themeTokens.color.text.success}}>
        {DateFormatter.dateToShortFormat(latestOf(clusters.map(c => c.CompletedAt)))}
    </span>;
}

// The reef reached the version when its last cluster did. ISO-8601 sorts lexicographically.
function latestOf(timestamps: (string | undefined)[]): string | undefined {
    return timestamps.filter((t): t is string => !!t).sort().pop();
}

export default HostedScriptsRollout;
